import { useState, useCallback, memo } from 'react';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useAppSelector } from 'app';
import AppSnackbar from 'components/snackbar/snackbar';
import config from 'config';
import DepartmentAPI from 'components/setting/api/department-api';
import type { LinkFacebookAccountRequest } from 'components/setting/api/department-api';
import { useFacebookTokenContext } from 'common/context/FacebookTokenContext';
import { useSocialTestDealer } from 'common/context/SocialTestDealerContext';

interface FacebookLoginButtonProps {
  isAccountConnected?: boolean;
  setIsAccountConnected: (value: boolean) => void;
  locationId: number | undefined;
  onRefresh: () => void;
  parentLoading?: boolean;
}

const FacebookLoginButton: React.FC<FacebookLoginButtonProps> = memo(({ 
  onRefresh, 
  isAccountConnected, 
  setIsAccountConnected, 
  locationId, 
  parentLoading 
}) => {
  const [showSnackbar] = AppSnackbar.useSnackBar();
  const dealerId = useAppSelector((state) => state.user.dealerId);
  const dmsDealerId = useAppSelector((state) => state.user.dmsDealerId);
  const crmUserId = useAppSelector((state) => state.user.crmUserId);
  const facebookAppId = config.get('api.facebookAppId');
  const [loading, setLoading] = useState<boolean>(false);
  const { isSocialTestDealer, isCheckingSocialTestDealer } = useSocialTestDealer();
  const loadingState = loading || parentLoading || isCheckingSocialTestDealer;
  
  const { incrementTokenVersion } = useFacebookTokenContext();

  const responseFacebook = useCallback(async (response: any) => {
    // Facebook Login Status Types:
    // 'connected' - User is logged into Facebook AND has authorized your app
    // 'not_authorized' - User is logged into Facebook but has NOT authorized your app
    // 'unknown' - User is not logged into Facebook OR cancelled the popup
    // undefined/empty - User closed the popup without completing login

    // Early exit if no response or user cancelled the popup
    if (!response || Object.keys(response).length === 0) {
      setLoading(false);
      return; // User closed popup - silent exit
    }

    // Handle explicit status responses
    if (response.status === 'unknown') {
      setLoading(false);
      return; // User cancelled or not logged in - silent exit
    }

    if (response.status === 'not_authorized') {
      setLoading(false);
      showSnackbar({
        type: "warning",
        message: "Please authorize the app to continue with Facebook login.",
      });
      return;
    }

    // At this point, status should be 'connected' - validate we have required data
    if (!response.accessToken) {
      setLoading(false);
      showSnackbar({
        type: "error",
        message: "Failed to get Facebook access token. Please try again.",
      });
      return;
    }

    if (!response.userID) {
      setLoading(false);
      showSnackbar({
        type: "error",
        message: "Unable to retrieve Facebook user information. Please try again.",
      });
      return;
    }

    try {

      const linkFbAccountRequest: LinkFacebookAccountRequest = {
        dealerId: dealerId,
        locationId: locationId ?? 0,
        userAccessToken: response.accessToken,
        facebookUserId: response.userID,
        facebookEmail: response.email ?? '',
        facebookUserName: response.name ?? '',
        crmUserId: crmUserId
      };
      
      await DepartmentAPI.linkFacebookAccount(linkFbAccountRequest);
      showSnackbar({ type: 'success', message: 'Facebook Account linked successfully.' });
      setIsAccountConnected(true);
      incrementTokenVersion(); // Notify context consumers (like TokenExpiredBanner)
      onRefresh();
      
    } catch (error) {
      let errorMsg = 'An error occurred while linking Facebook account.';
      if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMsg = (error as { message: string }).message;
      }
      showSnackbar({ type: 'error', message: errorMsg });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, dealerId, crmUserId]);

  // Hide button if dealer is not in the test dealer list
  if (!isCheckingSocialTestDealer && !isSocialTestDealer) {
    return null;
  }

  return (
    <FacebookLogin
      appId={facebookAppId}
      autoLoad={false}
      fields="name,email,picture"
      scope="public_profile,email,pages_show_list,pages_messaging,instagram_basic,instagram_manage_messages,pages_manage_metadata"
      callback={responseFacebook}
      render={(renderProps: { onClick: () => void }) => (
        <Button
          variant="outlined"
          color="primary"
          disabled={loadingState}
          onClick={() => {
            if (!locationId || locationId === 0) {
              showSnackbar({
                type: "warning",
                message: "Please select a valid location before linking Facebook account.",
              });
              return;
            }
            setLoading(true);
            renderProps.onClick();
          }}
          endIcon={isAccountConnected ? <CheckCircleIcon color="success" /> : undefined}
        >
          {loadingState ? <CircularProgress size={20} color="inherit" /> : 'Link Facebook'}
        </Button>
      )}
    />
  );
});

FacebookLoginButton.displayName = 'FacebookLoginButton';

export default FacebookLoginButton;
